import React from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Calendar, ExternalLink, MessageCircle, Send, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

function PostDetail() {
  const { postId } = useParams();
  const [post, setPost] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [commentText, setCommentText] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    async function load() {
      try {
        const p = await axios.get(API + '/posts/' + postId);
        const c = await axios.get(API + '/posts/' + postId + '/comments');
        setPost(p.data);
        setComments(c.data);
        
        try {
          const u = await axios.get(API + '/auth/me', { withCredentials: true });
          setUser(u.data);
        } catch (e) {
          setUser(null);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    }
    load();
  }, [postId]);

  async function submitComment(e) {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(API + '/posts/' + postId + '/comments', 
        { content: commentText }, 
        { withCredentials: true }
      );
      setComments([res.data].concat(comments));
      setCommentText('');
    } catch (error) {
      alert('Erro ao postar comentário');
    }
    setSubmitting(false);
  }

  async function deleteComment(cid) {
    if (!confirm('Deletar comentário?')) return;
    try {
      await axios.delete(API + '/comments/' + cid, { withCredentials: true });
      setComments(comments.filter(c => c.comment_id !== cid));
    } catch (error) {
      alert('Erro ao deletar');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} />
        <div className="text-center py-20">
          <p className="text-lg text-stone-600">Artigo não encontrado</p>
          <Link to="/" className="text-primary hover:underline mt-4 inline-block">Voltar</Link>
        </div>
      </div>
    );
  }

  const cleanContent = String(post.content || '').replace(/<[^>]*>/g, '');
  
  // Extrair ID do vídeo do YouTube
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  const youtubeId = getYouTubeId(post.youtube_url);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />
      <article className="max-w-3xl mx-auto px-6 py-12">
        {post.is_rss && post.source_name && (
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="w-4 h-4 text-stone-500" />
            <span className="text-sm text-stone-500 uppercase tracking-wider font-medium">
              {post.source_name}
            </span>
          </div>
        )}

        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-6">
          {post.title}
        </h1>

        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-stone-200">
          <div className="flex items-center gap-2 text-stone-600">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{new Date(post.published_at).toLocaleDateString('pt-BR')}</span>
          </div>
        </div>

        {post.image_url && (
          <div className="mb-8 rounded-lg overflow-hidden">
            <img src={post.image_url} alt={post.title} className="w-full" />
          </div>
        )}

        {youtubeId && (
          <div className="mb-8 rounded-lg overflow-hidden" style={{position: 'relative', paddingBottom: '56.25%', height: 0}}>
            <iframe
              style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}}
              src={`https://www.youtube.com/embed/${youtubeId}`}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <div className="prose prose-lg max-w-none mb-12 text-stone-700">
          <p className="whitespace-pre-wrap">{cleanContent}</p>
        </div>

        {post.is_rss && post.source_url && (
          <div className="mb-12 p-6 bg-stone-50 rounded-lg border">
            <a href={post.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
              <ExternalLink className="w-4 h-4" />
              Ler artigo original
            </a>
          </div>
        )}

        <div className="border-t pt-12">
          <div className="flex items-center gap-2 mb-6">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-heading font-semibold">Comentários ({comments.length})</h2>
          </div>

          {user ? (
            <form onSubmit={submitComment} className="mb-8">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Deixe seu comentário..."
                className="w-full p-3 border rounded-md mb-3 min-h-[100px]"
                rows={4}
              />
              <button
                type="submit"
                disabled={submitting || !commentText.trim()}
                className="px-4 py-2 bg-primary text-white rounded-md hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Comentar
              </button>
            </form>
          ) : (
            <div className="mb-8 p-6 bg-stone-50 rounded-lg border">
              <p className="text-stone-600 mb-3">Cadastre-se ou faça login para comentar</p>
              <Link to="/login">
                <button className="px-4 py-2 bg-primary text-white rounded-md hover:opacity-90">
                  Criar Conta / Entrar
                </button>
              </Link>
            </div>
          )}

          <div className="space-y-6">
            {comments.length === 0 && (
              <p className="text-stone-500 text-center py-8">Nenhum comentário ainda</p>
            )}
            {comments.map((comment) => (
              <div key={comment.comment_id} className="p-6 bg-white rounded-lg border">
                <div className="flex justify-between mb-3">
                  <div>
                    <p className="font-medium">{comment.user_name}</p>
                    <p className="text-sm text-stone-500">{new Date(comment.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  {user && user.user_id === comment.user_id && (
                    <button
                      onClick={() => deleteComment(comment.comment_id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-stone-700">{comment.content}</p>
              </div>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}

export default PostDetail;
